/**
 * Polish. The default locale: this is a Polish dog breeder's tool first.
 *
 * Keys are nested by area and named for what the string *means*, never for what
 * it says — `offspring.weightMissing`, not `offspring.noWeightYet`. A key named
 * after its English text has to be renamed the day the copy changes, and
 * renaming a key means touching every locale.
 *
 * The key names are species-neutral, the words are not: `offspring.title` reads
 * "Szczenięta". See en.ts.
 */
export const pl = {
  common: {
    save: 'Zapisz',
    cancel: 'Anuluj',
    edit: 'Edytuj',
    delete: 'Usuń',
    back: 'Wstecz',
    close: 'Zamknij',
    loading: 'Wczytywanie…',
    offline: 'Brak połączenia',
    unsynced: 'Zapisano na telefonie — czeka na synchronizację',
    tryAgain: 'Spróbuj ponownie',
    optional: 'opcjonalne',
    /** Kto to zapisał, kiedy nie wiadomo już kto. Zob. `useMemberName`. */
    someone: 'Ktoś z hodowli',
    /** Miniona chwila powiedziana po ludzku: nazwa dnia, potem godzina. Zob. en.ts. */
    dayAtTime: '{{day}} o {{time}}',
  },
  /**
   * Chrome wspólne dla kreatorów pełnoekranowych — ważenia i zdjęć. Osobna
   * gałąź, bo te słowa nie należą ani do jednego, ani do drugiego: zob.
   * `components/wizard.tsx`.
   */
  wizard: {
    changeWhen: 'Zmień datę',
    dateLabel: 'Data',
    timeLabel: 'Godzina',
  },
  auth: {
    signIn: 'Zaloguj się przez Google',
    signOut: 'Wyloguj',
    tagline: 'Waga i zdrowie szczeniąt, od pierwszego dnia.',
    signedInAs: 'Zalogowano jako {{email}}',
    failed: 'Logowanie się nie udało. Sprawdź połączenie i spróbuj ponownie.',
    signingIn: 'Logowanie…',
  },
  start: {
    title: 'Zacznij własną hodowlę',
    lead: 'Hodowla to wspólne miejsce na mioty, wagi i historię zdrowia. Zaprosisz do niej rodzinę i weterynarza, kiedy będziesz gotowa.',
    nameLabel: 'Nazwa hodowli',
    namePlaceholder: 'Zielona Dolina',
    breedLabel: 'Rasa',
    breedPlaceholder: 'Welsh Corgi Pembroke',
    countryLabel: 'Kraj',
    submit: 'Załóż hodowlę',
    creating: 'Zakładamy hodowlę…',
    needsName: 'Podaj nazwę hodowli.',
    failed: 'Nie udało się założyć hodowli. Spróbuj ponownie.',
    takingLonger: 'To czasem trwa dłużej niż zwykle. Prosimy o cierpliwość.',
  },
  invite: {
    waiting_one: 'Czeka na Ciebie zaproszenie',
    waiting_few: 'Czekają na Ciebie zaproszenia',
    waiting_many: 'Czeka na Ciebie {{count}} zaproszeń',
    asRole: 'jako {{role}}',
    accept: 'Dołącz do hodowli',
    accepting: 'Dołączamy…',
    acceptFailed: 'Nie udało się dołączyć. Zaproszenie mogło wygasnąć — poproś o nowe.',
    panelTitle: 'Zaproszenia',
    add: 'Zaproś osobę',
    emailLabel: 'Adres e-mail',
    emailPlaceholder: 'ala@example.com',
    roleLabel: 'Rola',
    scopesLabel: 'Co zobaczy',
    needsScope: 'Zaznacz przynajmniej jedną rzecz do pokazania.',
    send: 'Zaproś',
    sending: 'Wysyłamy…',
    sendFailed: 'Nie udało się utworzyć zaproszenia. Spróbuj ponownie.',
    needsEmail: 'Podaj adres e-mail.',
    selfEmail: 'To Twój własny adres.',
    hint: 'Osoba dołączy, kiedy zaloguje się przez Google tym samym adresem.',
    none: 'Nikt nie czeka na dołączenie.',
    revokeShort: 'Cofnij',
    sentAt: 'Zaproszenie wysłane {{date}}',
    sendFailedStatus: 'Nie udało się wysłać zaproszenia. Spróbuj ponownie albo przekaż link.',
    sendFailedShort: 'Nie wysłano',
    resend: 'Wyślij ponownie',
    resendFailed: 'Nie udało się wysłać. Sprawdź adres albo przekaż link.',
    copyLink: 'Kopiuj link',
    linkCopied: 'Skopiowano',
    revoke: 'Cofnij zaproszenie',
    revokeTitle: 'Cofnąć zaproszenie?',
    revokeBody: '{{email}} nie będzie mogła dołączyć tym zaproszeniem. Możesz wysłać nowe.',
    expiresIn_one: 'Wygasa za {{count}} dzień',
    expiresIn_few: 'Wygasa za {{count}} dni',
    expiresIn_many: 'Wygasa za {{count}} dni',
    expired: 'Wygasło',
  },
  /**
   * Ekran, na który prowadzi link z zaproszenia — patrz routes/join.$inviteId.tsx.
   * Osoba, która go otwiera, zwykle nie ma jeszcze konta w tej aplikacji.
   */
  join: {
    title: 'Zaproszenie do hodowli',
    signInLead: 'Zaloguj się, żeby zobaczyć zaproszenie i dołączyć do hodowli.',
    sameAddress: 'Użyj tego samego adresu e-mail, na który przyszło zaproszenie.',
    missingTitle: 'Nie znaleźliśmy tego zaproszenia',
    missingLead: 'Dla adresu {{email}} nie ma tu zaproszenia. Mogło zostać cofnięte, mogło wygasnąć albo jest wystawione na inny adres.',
    expiredLead: 'To zaproszenie wygasło. Poproś o nowe.',
    useAnotherAccount: 'Zaloguj się innym kontem',
    toApp: 'Przejdź do aplikacji',
  },
  member: {
    title: 'Osoby',
    accountTitle: 'Twoje konto',
    you: 'To Ty',
    since: 'W hodowli od {{date}}',
    ownerOnly: 'Zapraszać i zmieniać role może tylko właściciel.',
    roleLabel: 'Rola',
    changeRole: 'Zmień rolę',
    changeRoleDone: 'Gotowe',
    remove: 'Usuń',
    removeTitle: 'Usunąć {{name}} z hodowli?',
    removeBody: '{{name}} straci dostęp do miotów, wag i historii zdrowia. Zapisane dane zostają. Możesz zaprosić tę osobę ponownie.',
    changeFailed: 'Nie udało się zmienić. Hodowla musi mieć co najmniej jednego właściciela.',
    empty: 'Nikogo tu jeszcze nie ma.',
  },
  role: {
    owner: 'Właściciel',
    handler: 'Hodowca',
    viewer: 'Obserwator',
    ownerWhat: 'Zarządza hodowlą i osobami',
    handlerWhat: 'Prowadzi mioty, wagi i zdrowie',
    viewerWhat: 'Widzi wybrane części miotu',
  },
  /**
   * Co widzi obserwator — po jednej linijce na część zapisu miotu. To nazwy
   * dwóch sekcji na stronie miotu, żeby pola, które zaznacza właściciel, i
   * nagłówki, które czyta obserwator, były tymi samymi słowami. Zobacz
   * `VIEW_SCOPES` w platform/src/roles.ts.
   */
  scope: {
    measurements: 'Wagi i wzrost',
    photos: 'Oś czasu zdjęć',
    tasks: 'Zadania',
  },
  nav: {
    litters: 'Mioty',
    offspring: 'Szczenięta',
    people: 'Osoby',
    breadcrumb: 'Ścieżka nawigacji',
  },
  /**
   * Kolory obroży. Rodzaj żeński, bo opisują obrożę — i dlatego mogą stanąć
   * same za imię szczeniaka, które go jeszcze nie ma („Niebieska”).
   */
  collar: {
    red: 'Czerwona',
    orange: 'Pomarańczowa',
    yellow: 'Żółta',
    green: 'Zielona',
    blue: 'Niebieska',
    violet: 'Fioletowa',
    pink: 'Różowa',
  },
  sex: {
    female: 'Suczka',
    male: 'Piesek',
  },
  litter: {
    title: 'Mioty',
    add: 'Nowy miot',
    addFirst: 'Dodaj pierwszy miot',
    empty: 'Nie ma jeszcze żadnego miotu',
    emptyHint: 'Zacznij od miotu — potem dodasz do niego szczenięta, ich wagi i zdrowie.',
    emptyReadOnly: 'Mioty zakłada właściciel albo hodowca.',
    readOnly: 'Miot może założyć tylko właściciel albo hodowca.',
    newTitle: 'Nowy miot',
    step: 'Krok {{current}} z {{total}}',
    nameLabel: 'Nazwa miotu',
    namePlaceholder: 'Miot A',
    nameHint: 'Po tym znajdziesz go na liście — litera miotu albo imiona rodziców.',
    needsName: 'Podaj nazwę miotu.',
    needsDate: 'Podaj datę.',
    statusLabel: 'Status',
    status: {
      active: 'Aktualny',
      incoming: 'Oczekiwany',
      historical: 'Archiwalny',
    },
    statusWhat: {
      active: 'Szczenięta są w domu i rosną.',
      incoming: 'Suka jest w ciąży — szczeniąt jeszcze nie ma.',
      historical: 'Miot zamknięty: wszystkie szczenięta odeszły albo zostały u Ciebie.',
    },
    dueLabel: 'Termin porodu',
    bornLabel: 'Urodzenie',
    // Obie połówki jednej chwili, opisane dla czytnika ekranu i schowane na
    // ekranie: legenda `Urodzenie` nad nimi już nazywa tę parę. Zob. `TextField`.
    bornDateLabel: 'Data urodzenia',
    bornTimeLabel: 'Godzina urodzenia',
    bornTimeHint:
      'Godzina ma znaczenie: pierwsze dwie doby oceniamy w oknach dwudziestoczterogodzinnych, a data bez godziny myli wiek nawet o dwanaście z nich. Przybliżona godzina jest lepsza niż dokładna północ.',
    damLabel: 'Matka',
    damHint: 'Wpisz imię. Jeśli nie ma jej jeszcze w hodowli, dodamy ją.',
    damWeightLabel: 'Waga suki przed kryciem (kg)',
    damWeightPlaceholder: '12,4',
    damWeightHint:
      'Ile ważyła, zanim zaszła w ciążę. Po tym poznajemy niską wagę urodzeniową — progi pierwszego tygodnia są dla drobnego noworodka dużo ostrzejsze.',
    needsDamWeight: 'Podaj wagę w kilogramach, najwyżej {{max}}.',
    sireLabel: 'Ojciec',
    sireHint: 'Wpisz imię. Jeśli nie ma go jeszcze w hodowli, dodamy go.',
    breedLabel: 'Rasa',
    breedHint: 'Zapisujemy ją przy miocie, więc pozostanie ta sama, nawet jeśli hodowla zmieni rasę wiodącą.',
    countLabel: 'Liczba szczeniąt',
    countHint: 'Tyle kart otworzy się w następnym kroku. Możesz je tam dodać albo usunąć.',
    countExpectedLabel: 'Spodziewana liczba szczeniąt',
    countExpectedHint: 'Sama liczba — szczenięta dodasz, kiedy się urodzą.',
    countStoredHint: 'Ile szczeniąt było spodziewanych. Porównujemy z tym, ile jest w miocie.',
    saveIncoming: 'Zapisz miot',
    toOffspring: 'Dalej: szczenięta',
    bornOn: 'Urodzone {{date}}',
    dueOn: 'Termin {{date}}',
    born: 'Urodzone',
    due: 'Termin',
    parents: 'Rodzice',
    countLabelShort: 'Liczba',
    countVsExpected: '{{count}} z {{expected}} spodziewanych',
    count_one: '{{count}} szczenię',
    count_few: '{{count}} szczenięta',
    count_many: '{{count}} szczeniąt',
    expected_one: 'Spodziewane {{count}} szczenię',
    expected_few: 'Spodziewane {{count}} szczenięta',
    expected_many: 'Spodziewanych {{count}} szczeniąt',
    missing: 'Nie ma takiego miotu.',
  },
  animal: {
    unknown: 'Nie wiem',
    namePlaceholder: {
      female: 'Maja',
      male: 'Rex',
    },
  },
  offspring: {
    title: 'Szczenięta',
    empty: 'Brak szczeniąt w tym miocie',
    emptyWhere: 'Szczenięta dodasz w edycji miotu — ołówkiem przy jego nazwie.',
    emptyIncoming: 'Szczeniąt jeszcze nie ma. Dodasz je, kiedy się urodzą.',
    missing: 'Nie ma takiego szczenięcia.',
    unnamed: 'Bez imienia',
    add: 'Dodaj',
    addAnother: 'Dodaj kolejne szczenię',
    remove: 'Usuń szczenię',
    deleteTitle: 'Usunąć {{name}}?',
    deleteBody: 'Razem ze szczenięciem znikną jego wagi, zdarzenia zdrowotne i zdjęcia. Tego nie da się cofnąć.',
    edit: 'Edytuj: {{name}}',
    open: 'Otwórz: {{name}}',
    ordinal: 'Szczenię {{index}}',
    stepLead: 'Przy każdym potrzebna jest obroża i płeć. Imię i zdjęcia możesz dodać później.',
    sexLabel: 'Płeć',
    statusLabel: 'Status',
    nameLabel: 'Imię',
    namePlaceholder: 'Bella',
    nameHint: 'Możesz zostawić puste — szczenię rozpoznasz po obroży.',
    birthWeightLabel: 'Waga urodzeniowa (g)',
    birthWeightHint:
      'Ile szczenię ważyło po urodzeniu — osuszone, przed pierwszym karmieniem. Trzy kontrole wzrostu liczą się od niej, a drugi raz zmierzyć jej się nie da.',
    birthWeightIncomingHint: 'Szczeniąt jeszcze nie ma — wagę dodasz, kiedy się urodzą.',
    birthWeightMissing: 'Nie zapisano',
    needsBirthWeight: 'Podaj wagę urodzeniową w gramach.',
    collarLabel: 'Obroża',
    collarHint: 'Po tym kolorze rozpoznasz szczenię, zanim dostanie imię.',
    collarTaken: 'już zajęta',
    needsCollar: 'Wybierz kolor obroży.',
    needsSex: 'Zaznacz płeć.',
    photoLabel: 'Zdjęcie główne',
    photoHint: 'Po nim rozpoznasz szczenię na liście i przy wagach.',
    photoReplaceHint: 'Dotknij zdjęcia, żeby wybrać inne.',
    photoFailed: 'Nie udało się wysłać zdjęcia. Szczenię zapisze się bez niego — dodasz je później.',
    photoUploading: 'Wysyłanie…',
    photoAdd: 'Dodaj zdjęcie',
    photoReplace: 'Zmień zdjęcie',
    photoRemove: 'Usuń zdjęcie',
    incomplete_one: '{{count}} szczenię czeka na obrożę, płeć albo wagę urodzeniową.',
    incomplete_few: '{{count}} szczenięta czekają na obrożę, płeć albo wagę urodzeniową.',
    incomplete_many: '{{count}} szczeniąt czeka na obrożę, płeć albo wagę urodzeniową.',
    saveAll_one: 'Zapisz {{count}} szczenię',
    saveAll_few: 'Zapisz {{count}} szczenięta',
    saveAll_many: 'Zapisz {{count}} szczeniąt',
    status: {
      raising: 'Odchowywane',
      reserved: 'Zarezerwowane',
      placed: 'Wydane',
      kept: 'Zostaje',
      deceased: 'Padło',
    },
    ageDays_one: '{{count}} dzień',
    ageDays_few: '{{count}} dni',
    ageDays_many: '{{count}} dni',
    weight: 'Waga',
    weightMissing: 'Bez wagi',
    recordWeight: 'Zapisz wagę',
    losingWeight: 'Traci na wadze — sprawdź szczenię',
    weighPuppies: 'Zważ szczenięta',
    weighTitle: 'Ważenie szczeniąt',
    weighProgress: 'Zważono {{count}} z {{total}}',
    weighPending: 'Do zważenia',
    weighed: 'Zważone',
    weighEdit: 'Edytuj wagę: {{name}}',
    weighUnitGrams: 'g',
    weighPrevious: 'Wcześniej',
    weighInvalidGrams: 'Podaj wagę w gramach.',
    weighToday: 'Zapisze się jako dzisiejsze ważenie, godz. {{time}}.',
    weighBackdated: 'Zapisze się jako ważenie z dnia {{date}}, godz. {{time}}.',
    weighReviewTitle: 'Potwierdź wagi',
    weighReviewLead: 'Sprawdź każde szczenię przed zapisaniem.',
    weighPartialLead: 'Zapiszą się szczenięta z wagą. Resztę zważysz, kiedy będą pod ręką.',
    weighConfirm: 'Zapisz wszystkie wagi',
    weighConfirmSome_one: 'Zapisz {{count}} wagę',
    weighConfirmSome_few: 'Zapisz {{count}} wagi',
    weighConfirmSome_many: 'Zapisz {{count}} wag',
    weighDiscard: 'Odrzuć bez zapisywania',
    weighLeaveTitle: 'Odrzucić to ważenie?',
    weighLeaveBody: 'Wpisane dotąd wagi przepadną. Nic nie zostało jeszcze zapisane.',
    weighLeaveCancel: 'Ważę dalej',
  },
  /**
   * Monitorowanie wzrostu: wykres i tabela na stronie miotu i szczenięcia.
   *
   * Klucze `status.*` i `advice.*` odpowiadają `GrowthStatus` z platform/growth.ts
   * jeden do jednego — status bez porady to sam kolor, a kolor nic nie mówi.
   */
  growth: {
    title: 'Wzrost',
    empty: 'Zapisz pierwsze wagi, żeby zobaczyć wzrost.',
    view: {
      chart: 'Wykres',
      table: 'Tabela',
    },
    day: 'Dzień {{day}}',
    dayShort: '{{day}} d.',
    // Pierwszy punkt wykresu i ostatni wiersz tabeli: nie ważenie, tylko waga
    // urodzeniowa. Model z niej liczy trzy swoje progi, ale jej samej nie ocenia.
    birthWeight: 'Waga urodzeniowa',
    grams: '{{grams}} g',
    delta: '{{grams}} g',
    percent: '{{percent}}%',
    // Liczba, z której naprawdę bierze się ocena: regresja po wszystkich
    // ważeniach z ostatnich 48 (a po trzech tygodniach 72) godzin, w procentach
    // na dobę. Świadomie nie „na dobę” jak dawniej — tamten klucz znaczył różnicę
    // między dwoma ważeniami i użycie tego samego słowa dla trendu przeniosłoby
    // jedno czytanie sekcji na drugie.
    trend: 'trend {{percent}}%/dobę',
    // Z którego ważenia policzony jest przyrost w wierszu — z tego nad nim, a
    // przy ważeniu dwa razy dziennie to może być osiem godzin, nie doba.
    since: 'od {{date}}',
    lastWeighed: 'ostatnie ważenie {{date}}',
    // Dwie liczby, które porównała reguła, pod zdaniem, które ją cytuje.
    // „−4,7% wobec −4,1%” — wartość zaobserwowana i próg, w jednostce, w jakiej
    // próg jest opublikowany. Zob. components/growth/reasons.ts.
    against: '{{observed}} wobec {{threshold}}',
    // Co stoi w miejscu nazwiska autorów przy regule, która żadnego nie ma.
    sourceHeuristic: 'reguła praktyczna, bez opublikowanego progu',
    showBands: 'Pokaż zakres odniesienia',
    wellAll: 'Wszystkie szczenięta rosną prawidłowo.',
    wellOne: 'Rośnie prawidłowo.',
    checkNow: 'Pilnie sprawdź: {{names}}',
    checkNowOne: 'Sprawdź to pilnie.',
    watchThese: 'Zwróć uwagę na: {{names}}',
    watchThisOne: 'Miej to na oku.',
    // Prośba, nie ostrzeżenie — i tylko dopóki cokolwiek to zmieni. Bez wagi
    // suki model nie odróżni niskiej wagi urodzeniowej od normalnej, więc
    // stosuje łagodniejsze progi i mówi mniej.
    needsDamWeight:
      'Zapisz w ustawieniach miotu, ile suka ważyła przed kryciem. Po tym poznajemy niską wagę urodzeniową — a progi pierwszego tygodnia są dla drobnego noworodka dużo ostrzejsze.',
    openLitter: 'Przejdź do miotu',
    columnDay: 'Dzień',
    columnWeight: 'Waga (g)',
    columnGain: 'Przyrost',
    columnTrend: 'Trend',
    columnStatus: 'Ocena',
    disclaimer: 'To narzędzie do monitorowania, a nie diagnoza. W razie wątpliwości skontaktuj się z weterynarzem.',
    /**
     * Pięć ocen. Krótkie na tyle, żeby zmieściły się w komórce tabeli, i tak
     * sformułowane, żeby `above` nie dało się wziąć za ostrzeżenie — badania, na
     * których stoi ten model, wprost mówią, że wzrost optymalny to nie to samo
     * co maksymalny.
     */
    status: {
      alarm: 'Poniżej progu ryzyka',
      below: 'Poniżej oczekiwanego zakresu',
      normal: 'Rośnie prawidłowo',
      above: 'Rośnie szybciej niż zakres',
      insufficientData: 'Za mało danych, by ocenić',
    },
    advice: {
      alarm: 'Skontaktuj się z weterynarzem. Sprawdź, czy szczenię ssie i czy jest ciepłe, rozważ dokarmianie i waż co 6–8 godzin, dopóki się nie odbije.',
      below: 'Obserwuj przy każdym ważeniu. Sprawdź, czy dochodzi do smoczka i czy nie jest odpychane, sprawdź temperaturę gniazda, rozważ dokarmianie.',
      normal: 'Nic nie trzeba robić.',
      above: 'Nic nie trzeba robić.',
      insufficientData: 'Zapisz kolejne ważenie.',
    },
    /** Reżim, w który wiek szczenięcia wstawia model. Zob. `GrowthPhase`. */
    phase: {
      earlyNeonatal: 'pierwsze 48 godzin',
      neonatal: 'krzywa odniesienia',
      postNeonatal: 'brak opublikowanego odniesienia',
    },
    confidence: {
      high: 'wysoka pewność',
      medium: 'średnia pewność',
      low: 'niska pewność',
    },
    /**
     * Dopisek pod alarmem, którego model nie jest pewien — i tylko tam. Wszystko
     * inne poniżej wysokiej pewności po prostu się nie pokazuje, więc te dwa
     * zdania są po to, żeby telefon do weterynarza o czwartej rano nie opierał
     * się na przesłankach mocniejszych, niż czytelnik sądzi. Nie ma wpisu dla
     * `high`: przy wysokiej pewności nic się nie rysuje, a pusty napis w drzewie
     * fraz to napis, który ktoś kiedyś wyświetli.
     */
    confidenceNote: {
      medium: 'Policzone z mniejszej liczby ważeń, niż byśmy chcieli, albo z reguły, za którą nie stoi opublikowany próg.',
      low: 'Policzone z bardzo niewielu danych: za mało ważeń, za krótka przerwa między nimi albo wiek, dla którego ten model nie ma odniesienia.',
    },
    /**
     * Po jednym zdaniu na regułę, która może się odezwać, językiem modelu. Kod
     * jest kluczem; dwie porównane liczby dopisuje pod spodem `against`.
     *
     * Każde mówi, CO WIDAĆ, a nie co robić — zalecenie jest jedno, po ocenie, a
     * powtarzanie go przy każdej regule dałoby szczenięciu z trzema uwagami trzy
     * polecenia.
     */
    reason: {
      gr01BelowThreshold: 'W pierwszej dobie straciło więcej niż noworodki, którym się udaje.',
      gr01Negative: 'Po dobie waży mniej niż przy urodzeniu. Mieści się w normie, ale warto zajrzeć.',
      gr12BelowThreshold: 'Straciło na wadze przez drugą dobę, poniżej progu wiązanego z ryzykiem.',
      gr02BelowThreshold: 'Po 48 godzinach jest poniżej wagi urodzeniowej bardziej niż noworodki, którym się udaje.',
      gr02BelowExpected: 'Po 48 godzinach wciąż jest poniżej wagi urodzeniowej bardziej, niż powinno.',
      lossVsBirth: 'Straciło z wagi urodzeniowej więcej, niż noworodek powinien przez pierwsze dwie doby.',
      awaitingFirstDay: 'Urodzone w ciągu ostatniej doby i jeszcze nieważone.',
      sgrRatioAlarm: 'Rośnie dużo wolniej, niż mówi krzywa odniesienia dla tego wieku.',
      sgrRatioBelow: 'Rośnie wolniej, niż mówi krzywa odniesienia dla tego wieku.',
      sgrRatioNormal: 'Rośnie w tempie, jakiego oczekuje krzywa odniesienia.',
      sgrRatioAbove: 'Rośnie szybciej, niż oczekuje krzywa odniesienia.',
      weightLoss24h: 'Straciło na wadze przez ostatnią dobę.',
      weightLoss48h: 'Przez ostatnie dwie doby nie przybrało nic. Po drugiej dobie życia to już nie jest fizjologia.',
      mortalityRiskDay7: 'Pierwszy tydzień złożył się na mniej niż u noworodków, którym się udaje.',
      laggingLitter: 'Odstaje od rodzeństwa, licząc od własnej wagi urodzeniowej każdego z nich.',
      heuristicPhase: 'Po trzech tygodniach nie ma opublikowanej krzywej odniesienia; dalej idzie nasza własna reguła praktyczna.',
      postWeightLoss: 'Straciło na wadze przez ostatnie trzy doby.',
      postSgrFloor: 'Przez ostatnie trzy doby prawie nie przybiera.',
      litterRelativeBelow: 'Wyraźnie odstaje od rodzeństwa, licząc od własnej wagi urodzeniowej każdego z nich.',
      litterRelativeAbove: 'Wyraźnie wyprzedza rodzeństwo, licząc od własnej wagi urodzeniowej każdego z nich.',
      litterDrift: 'Z tygodnia na tydzień odstaje od rodzeństwa coraz bardziej.',
      birthWeightCategoryUnknown:
        'Nie wiemy, czy to była niska waga urodzeniowa, więc stosujemy łagodniejsze progi.',
      noBirthWeight: 'Brak wagi urodzeniowej — wszystkie kontrole pierwszego tygodnia liczą się od niej.',
      notEnoughWeighings: 'Za mało ważeń dostatecznie blisko siebie, żeby odczytać trend.',
      staleWeighings: 'Policzone z ważeń, które nie są już świeże.',
      outOfModelRange: 'Po ośmiu tygodniach. Wagi dalej się zapisują; przestajemy je oceniać.',
    },
    /**
     * Teoria pod sekcją, pod ikonką „i” przy tytule: co jest z czym porównywane,
     * skąd biorą się liczby i — to najważniejsze — dlaczego sekcja zwykle
     * milczy. Pisane dla kogoś, kto nie zna tej aplikacji i nie musi znać żadnej
     * arytmetyki. Wszystkie liczby wstawiane są z `DEFAULT_GROWTH_CONFIG`, więc
     * ten tekst nie może rozjechać się z tym, co aplikacja naprawdę robi.
     */
    guide: {
      open: 'Jak oceniamy wzrost',
      title: 'Jak oceniamy wzrost',
      lead: 'Każde szczenię oceniamy względem niego samego i względem tego, co badania mówią o noworodku w tym wieku. Pytanie jest zawsze to samo: jaką część własnej masy przybrało i w jakim czasie.',
      rateTitle: 'Liczy się tempo',
      rateBody: 'Dziesięć gramów u szczenięcia ważącego 100 g to bardzo dobra doba. Te same dziesięć gramów u szczenięcia ważącego 500 g to doba, po której trzeba do niego zajrzeć. Dlatego każdy próg tutaj to procent własnej masy na dobę.',
      rateBreed: 'Ten procent nie zależy od rasy. Noworodek przybiera około 13% siebie pierwszej doby i około 6% w trzecim tygodniu — niezależnie od tego, czy dorośnie do trzech, czy do czterdziestu kilogramów. Rasa zmienia gramy, nie procenty. Istotne jest to, że tempo spada z wiekiem, i właśnie tym jest krzywa poniżej.',
      windowTitle: 'Z czego bierze się jedna ocena',
      windowBody: 'Nie z dwóch ważeń, tylko ze wszystkich z ostatnich {{hours}} godzin — a po trzecim tygodniu, gdy zaczyna się odsadzanie, z {{late}} godzin. Przez te wagi prowadzimy prostą, a jej nachylenie to tempo wzrostu.',
      windowWhy: 'Waga noworodka waha się o trzy do ośmiu procent między jednym ważeniem a drugim z powodów, które ze wzrostem nie mają nic wspólnego: pełny żołądek, pełny pęcherz, wiercenie się na wadze. Prawdziwy dobowy przyrost to pięć do dziesięciu procent. Na przestrzeni jednej nocy szum jest tej samej wielkości co sygnał — dlatego ocena z jednego nocnego odstępu jest bliska rzutowi monetą, a ta nie jest.',
      phasesTitle: 'Trzy wieki, trzy różne pytania',
      phasesBody: 'Pierwsze dwie doby to nie wolniejsza wersja pierwszych dwóch tygodni, a drugi miesiąc to nie wolniejsza wersja pierwszego. Każdy ma własne reguły.',
      phaseAge: {
        earlyNeonatal: 'pierwsze {{early}} doby',
        neonatal: 'doby od {{early}} do {{neonatal}}',
        postNeonatal: 'doby od {{neonatal}} do {{end}}',
      },
      phaseWhat: {
        earlyNeonatal:
          'Spadek wagi jest tu normalny — pytanie brzmi tylko, jak duży. Progi są opublikowane i mierzone po 24 i 48 godzinach od wagi urodzeniowej.',
        neonatal:
          'Wzrost jest wykładniczy, z tempem malejącym z wiekiem, i istnieje dla niego opublikowana krzywa. Tu aplikacja stoi najpewniej, a siódma doba ma dodatkowo własny próg.',
        postNeonatal:
          'Dla tego odcinka nie ma opublikowanej krzywej odniesienia. Aplikacja opiera się na własnym trendzie szczenięcia i na porównaniu z rodzeństwem, i nigdy nie deklaruje tu więcej niż średniej pewności.',
      },
      birthWeightTitle: 'Niska waga urodzeniowa i po co pytamy o sukę',
      birthWeightBody: 'Drobny noworodek znosi w pierwszych 48 godzinach dużo mniejszy spadek niż normalny, a potem oczekuje się od niego, że nadrobi więcej. To dwa różne zestawy progów, a wybranie złego albo wywołuje fałszywe alarmy, albo przepuszcza prawdziwe.',
      birthWeightDam: 'Pewny sposób, żeby je rozróżnić bez tabeli dla każdej rasy, to waga szczenięcia jako procent wagi suki — od {{toy}}% przy suce miniaturowej do {{giant}}% przy olbrzymiej. Dlatego przy miocie pytamy, ile ważyła przed kryciem.',
      birthWeightFallback: 'Bez tego aplikacja opiera się na samym miocie: szczenię poniżej {{percent}}% mediany wag urodzeniowych rodzeństwa, w miocie liczącym co najmniej {{count}}. To nasza własna reguła praktyczna, a nie opublikowany próg — a gdy i tego nie ma, szczenię oceniamy łagodniejszymi progami, a sekcja mówi mniej.',
      hourTitle: 'Dlaczego godzina urodzenia ma znaczenie',
      hourBody: 'Pierwsze {{early}} doby oceniamy w oknach dwudziestoczterogodzinnych. Data bez godziny przesuwa wiek nawet o dwanaście godzin, czyli o pół okna — a wtedy te kontrole liczą się z niewłaściwej pary ważeń. Przybliżona godzina jest tu warta znacznie więcej niż precyzyjna północ.',
      meaningTitle: 'Co znaczą poszczególne oceny',
      meaning: {
        alarm:
          'Poniżej progu, który badania wiążą z podwyższoną śmiertelnością noworodków, albo spadek wagi po drugiej dobie życia. To jedyna ocena pokazywana niezależnie od tego, jak pewna jest aplikacja — a kiedy nie jest pewna, pisze o tym pod spodem.',
        below:
          'Poniżej oczekiwanego zakresu, ale powyżej progu ryzyka. Pokazywana tylko wtedy, gdy aplikacja jest pewna: dość ważeń, dostatecznie blisko siebie, w wieku, dla którego ma odniesienie.',
        normal: 'Rośnie w tempie oczekiwanym dla tego wieku. Nic nie trzeba robić.',
        above:
          'Rośnie szybciej niż zakres. To informacja, a nie ostrzeżenie: badania, na których to stoi, wprost mówią, że wzrost optymalny to nie to samo co maksymalny. Nigdy nie trafia do rzeczy do zrobienia.',
        insufficientData:
          'Reguł dla tego wieku nie dało się policzyć — za mało ważeń w oknie, brak wagi urodzeniowej albo szczenię po ośmiu tygodniach. Wagi i tak są zapisane i narysowane.',
      },
      confidenceTitle: 'Dlaczego ta sekcja zwykle milczy',
      confidenceBody: 'Każda ocena niesie ze sobą to, jak bardzo aplikacja jest jej pewna — i najczęściej to właśnie decyduje, czy w ogóle cokolwiek zostanie powiedziane. Miot rosnący zwyczajnie nie daje żadnej ramki nad wykresem. To znaczy, że sekcja działa, a nie że jest zepsuta.',
      confidenceWhat: {
        high: 'Co najmniej {{points}} ważenia rozłożone na {{hours}} godzin lub więcej, w wieku z opublikowanym odniesieniem, w miocie, dla którego dało się ustalić klasę wagi urodzeniowej. Tylko na tym poziomie zgłaszamy „poniżej zakresu”.',
        medium: 'Dwa ważenia w odstępie co najmniej {{minHours}} godzin, albo wiek po {{end}}. dobie, gdzie reguły są nasze własne, a nie opublikowane, albo szczenię o nieznanej klasie wagi urodzeniowej.',
        low: 'Bardzo niewiele: odstęp za krótki albo za długi, żeby odczytać z niego tempo, albo pierwsza doba, w której nic jeszcze nie zważono.',
      },
      confidenceAlarm: 'Alarm jest wyjątkiem i pokazuje się zawsze, na każdym poziomie pewności. To ocena wiązana ze śmiertelnością noworodków, a milczący alarm byłby wyborem porządku na ekranie zamiast tego, po co ta aplikacja jest.',
      corridorTitle: 'Pasmo na wykresie',
      corridorBody: 'Zacieniony pas pokazuje, gdzie szczenię powinno być doba po dobie, licząc od tego, ile naprawdę ważyło w {{anchor}}. dobie, i rosnąc od tego miejsca w tempie odniesienia. Kończy się w {{end}}. dobie, bo tam kończy się opublikowana krzywa.',
      corridorEdges: 'Jego krawędzie to te same dwie liczby, których używają oceny: {{low}}% i {{high}}% utrzymanego tempa odniesienia. Linia w paśmie to szczenię w zakresie — obraz i słowa nie mogą się rozjechać.',
      sourcesTitle: 'Skąd biorą się liczby',
      sourcesBody: 'Każdy próg pochodzi z opublikowanego badania prawdziwych miotów albo jest wszędzie, gdzie się pokazuje, oznaczony jako nasza własna reguła praktyczna.',
      sources: {
        alves: 'Alves (2020) — krzywa wzrostu pierwszych trzech tygodni, z 345 szczeniąt 19 ras.',
        mugnier: 'Mugnier i in. (2023) — progi ryzyka po 24 godzinach, 48 godzinach i siedmiu dobach, z 8550 szczeniąt ze 127 hodowli.',
        mila: 'Mila i in. (2015) — próg 48 godzin dla noworodka o normalnej wadze.',
        alonge: 'Alonge i in. (2023) — waga urodzeniowa jako procent wagi suki i to, co przy jej wielkości liczy się jako niska.',
      },
      limitsLead: 'Czego nie wie:',
      limits: {
        population: 'Badania dotyczą hodowli francuskich, włoskich i portugalskich. Sami autorzy zalecają ostrożność przy przenoszeniu tych liczb na inne populacje.',
        extrapolation: 'Krzywa odniesienia jest wyskalowana na dobach od drugiej do siódmej i przedłużona do {{end}}. To najsłabsze miejsce modelu.',
        heuristic: 'Wszystko po {{end}}. dobie to nasza własna reguła praktyczna. Nie ma dla tego opublikowanych progów.',
        supplementing: 'Aplikacja nie wie, czy szczenię jest dokarmiane butelką. Dokarmiane może wyglądać na rosnące prawidłowo, choć pod spodem coś jest nie tak — ocena to nie diagnoza.',
        firstWeighing: 'To, czy pierwsze ważenie było przed pierwszym ssaniem czy po nim, przesuwa wynik po 24 godzinach o kilka procent. Jeśli ważysz zawsze tak samo, przestaje to mieć znaczenie.',
      },
    },
  },
  /**
   * Historia ważeń pod wykresem: kto i kiedy zważył miot.
   *
   * Osobno od `growth.*`, bo to nie jest widok wzrostu — to zapis tego, co
   * zrobili ludzie. Klucze są neutralne gatunkowo, słowa nie: jedno ważenie
   * obejmuje cały miot naraz. Patrz `MeasurementLog.tsx`.
   */
  measurements: {
    title: 'Historia ważeń',
    lead: 'Kto i kiedy zapisał wagi. Stąd poprawisz albo usuniesz całe ważenie.',
    columnWhen: 'Ważenie',
    columnCount: 'Szczenięta',
    countOf: '{{weighed}} z {{total}}',
    columnWho: 'Zapisał(a)',
    edited: 'Poprawił(a) {{name}}, {{date}}',
    edit: 'Popraw ważenie z {{date}}',
    delete: 'Usuń ważenie z {{date}}',
    deleteTitle: 'Usunąć całe ważenie?',
    deleteBody_one: 'Znikną wagi {{count}} szczenięcia z dnia {{date}}. Tego nie da się cofnąć.',
    deleteBody_few: 'Znikną wagi {{count}} szczeniąt z dnia {{date}}. Tego nie da się cofnąć.',
    deleteBody_many: 'Znikną wagi {{count}} szczeniąt z dnia {{date}}. Tego nie da się cofnąć.',
    editTitle: 'Popraw ważenie',
    editLead: 'Zmień wagi albo datę tego ważenia. Zmiany zobaczą wszyscy w hodowli.',
    saveChanges: 'Zapisz zmiany',
    discardChanges: 'Odrzuć zmiany',
    discardChangesTitle: 'Odrzucić poprawki?',
    discardChangesBody: 'Ważenie zostanie takie, jak było zapisane.',
    discardChangesCancel: 'Poprawiam dalej',
  },
  /**
   * Historia sesji zdjęciowych — bliźniak `measurements.*`, bo sesja zdjęciowa
   * jest zapisana tak samo jak ważenie. Zob. `components/gallery/PhotoLog.tsx`.
   */
  photoLog: {
    title: 'Historia zdjęć',
    lead: 'Kto i kiedy dodał zdjęcia miotu. Stąd poprawisz albo usuniesz całą sesję.',
    columnWhen: 'Sesja',
    columnWho: 'Dodał(a)',
    edited: 'Poprawił(a) {{name}}, {{date}}',
    edit: 'Popraw sesję z {{date}}',
    delete: 'Usuń sesję z {{date}}',
    deleteTitle: 'Usunąć całą sesję?',
    deleteBody_one: 'Zniknie {{count}} zdjęcie z dnia {{date}}. Tego nie da się cofnąć.',
    deleteBody_few: 'Znikną {{count}} zdjęcia z dnia {{date}}. Tego nie da się cofnąć.',
    deleteBody_many: 'Zniknie {{count}} zdjęć z dnia {{date}}. Tego nie da się cofnąć.',
    editTitle: 'Popraw sesję zdjęciową',
    editLead: 'Zmień zdjęcia albo datę tej sesji. Zmiany zobaczą wszyscy w hodowli.',
    saveChanges: 'Zapisz zmiany',
    discardChanges: 'Odrzuć zmiany',
    discardChangesTitle: 'Odrzucić poprawki?',
    discardChangesBody: 'Sesja zostanie taka, jak była zapisana.',
    discardChangesCancel: 'Poprawiam dalej',
  },
  /**
   * Archiwum czytane jak album, a nie jak metryka: sesje na osi, od najnowszej,
   * każda to jeden dzień zdjęć. Osobno od `photoLog.*`, czyli tej samej kolekcji
   * czytanej jako zapis tego, co zrobili ludzie. Zob.
   * `components/gallery/PhotoTimeline.tsx`.
   */
  photoTimeline: {
    title: 'Oś czasu zdjęć',
    empty: 'Brak zdjęć tego miotu',
    // Ta sama oś na stronie jednego szczenięcia: tylko jego kadry, bez zdjęć
    // całego miotu. Zdjęcia dodaje się ze strony miotu, więc nie ma tu przycisku.
    emptySubject: 'Brak zdjęć tego szczenięcia',
    count_one: '{{count}} zdjęcie',
    count_few: '{{count}} zdjęcia',
    count_many: '{{count}} zdjęć',
    showMore_one: 'Pokaż {{count}} wcześniejszą sesję',
    showMore_few: 'Pokaż {{count}} wcześniejsze sesje',
    showMore_many: 'Pokaż {{count}} wcześniejszych sesji',
  },
  /**
   * Kreator zdjęć: jedna sesja zdjęciowa miotu, z datą. Odpowiednik ważenia —
   * zob. `components/PhotoWizard.tsx`.
   */
  photos: {
    title: 'Dodawanie zdjęć',
    progress_one: '{{count}} zdjęcie',
    progress_few: '{{count}} zdjęcia',
    progress_many: '{{count}} zdjęć',
    today: 'Zapiszą się jako zdjęcia z dzisiaj.',
    backdated: 'Zapiszą się jako zdjęcia z dnia {{date}}.',
    litterLabel: 'Cały miot',
    litterHint: 'Gniazdo, cała gromadka, suka z młodymi.',
    offspringLabel: 'Poszczególne szczenięta',
    save: 'Zapisz zdjęcia',
    /**
     * Napis na przycisku Zapisz, dopóki któreś zdjęcie jeszcze się wysyła —
     * przycisk jest wtedy nieaktywny. Mówi, na co czeka, a nie że coś jest nie
     * tak: to jedyne miejsce w aplikacji, które naprawdę potrzebuje sieci, więc
     * jedyne, w którym przycisk czeka. Zob. `components/PhotoWizard.tsx`.
     */
    saveWaiting_one: 'Wysyłanie {{count}} zdjęcia…',
    saveWaiting_few: 'Wysyłanie {{count}} zdjęć…',
    saveWaiting_many: 'Wysyłanie {{count}} zdjęć…',
    discard: 'Odrzuć bez zapisywania',
    leaveTitle: 'Odrzucić te zdjęcia?',
    leaveBody: 'Dodane dotąd zdjęcia przepadną. Nic nie zostało jeszcze zapisane.',
    leaveCancel: 'Dodaję dalej',
  },
  /**
   * Pojedyncze zdjęcie: przycisk dodawania, pasek wysyłki i otwarte powiększenie.
   * Osobno od `photos.*`, które prowadzi całą sesję, i od `photoTimeline.*`,
   * które opisuje oś czasu.
   *
   * `previousSession` i `nextSession` to dwa odnośniki na krańcach paska
   * miniatur. „Poprzednia” i „następna” znaczą tu to samo, co przy strzałkach
   * powyżej — kierunek przesuwania, a nie kolejność w czasie; oś czasu zaczyna
   * się od najnowszej sesji, więc poprzednia jest nowsza. Dzień i data mówią,
   * która to. Zob. `components/gallery/photoswipeThumbs.ts`.
   */
  gallery: {
    add: 'Dodaj zdjęcia',
    remove: 'Usuń zdjęcie',
    uploading: 'Wysyłanie…',
    failed: 'Nie udało się wysłać zdjęcia. Spróbuj ponownie.',
    lightbox: {
      close: 'Zamknij',
      zoom: 'Powiększ',
      previous: 'Poprzednie zdjęcie',
      next: 'Następne zdjęcie',
      error: 'Nie udało się wczytać zdjęcia',
      thumbnail: 'Zdjęcie {{number}} z {{total}}',
      previousSession: 'Poprzednia sesja: {{day}}, {{date}}',
      nextSession: 'Następna sesja: {{day}}, {{date}}',
    },
  },
  /**
   * Zdjęcia udostępnione do aplikacji z galerii telefonu — zainstalowana
   * aplikacja stoi w systemowym oknie „Udostępnij” obok komunikatorów. Zob.
   * `routes/share.tsx` i `offline/sharedPhotos.ts`.
   *
   * Ten ekran zadaje jedno pytanie, którego system nie przekazuje: do którego
   * miotu. Przy jednym miocie w domu nie pyta wcale — zdjęcia lądują od razu w
   * kreatorze, z dzisiejszą datą.
   */
  share: {
    title: 'Zdjęcia z galerii',
    pick_one: 'Masz tu {{count}} zdjęcie. Do którego miotu?',
    pick_few: 'Masz tu {{count}} zdjęcia. Do którego miotu?',
    pick_many: 'Masz tu {{count}} zdjęć. Do którego miotu?',
    empty: 'Nie ma tu żadnego zdjęcia',
    emptyHint: 'Dodasz je z albumu miotu, przyciskiem „Dodaj zdjęcia”.',
    readOnly: 'Zdjęcia dodaje właściciel albo hodowca.',
    readOnlyHint: 'Poproś o to osobę, która prowadzi hodowlę.',
    noLitter: 'Nie ma jeszcze miotu, do którego mogłyby trafić',
    noLitterHint: 'Załóż miot, a potem udostępnij zdjęcia jeszcze raz.',
    toLitters: 'Przejdź do miotów',
  },
  /**
   * Zadania przy miocie: co jest do zrobienia i kiedy, oraz co już zrobiono.
   *
   * `day` to dzień życia szczeniąt — ten sam sposób liczenia, co w sekcji
   * wzrostu, bo hodowca myśli terminami „czternasty dzień”, a nie datami w
   * kalendarzu. W formularzu stoją obok siebie dzień i data: wpisany jest jeden,
   * drugi wylicza się sam, a zapisuje się tylko data. Zob.
   * `components/tasks/TaskSection.tsx`.
   *
   * `today`, `tomorrow`, `ahead` i `overdue` to jedno zdanie na wiersz, po
   * terminie. Przeterminowane mówi, o ile — „po terminie” samo w sobie nie mówi,
   * czy chodzi o wczoraj, czy o trzy tygodnie temu.
   */
  tasks: {
    title: 'Zadania',
    add: 'Nowe zadanie',
    empty: 'Nic tu jeszcze nie zapisano.',
    emptyWhere: 'Zapisz, co i kiedy trzeba zrobić przy tym miocie.',
    textLabel: 'Co do zrobienia',
    textPlaceholder: 'Odrobaczenie całego miotu',
    needsText: 'Napisz, co jest do zrobienia.',
    dayLabel: 'Dzień życia',
    dateLabel: 'Termin',
    dueHint: 'Wpisz dzień życia szczeniąt albo datę — drugie wyliczy się samo.',
    needsDate: 'Wybierz termin.',
    day: 'Dzień {{day}}',
    today: 'Dziś',
    tomorrow: 'Jutro',
    ahead_one: 'Za {{count}} dzień',
    ahead_few: 'Za {{count}} dni',
    ahead_many: 'Za {{count}} dni',
    overdue_one: 'Po terminie o {{count}} dzień',
    overdue_few: 'Po terminie o {{count}} dni',
    overdue_many: 'Po terminie o {{count}} dni',
    markDone: 'Odhacz: {{text}}',
    markUndone: 'Cofnij odhaczenie: {{text}}',
    edit: 'Popraw zadanie',
    delete: 'Usuń zadanie',
    deleteTitle: 'Usunąć zadanie?',
    deleteBody: '„{{text}}” zniknie z listy. Tego nie da się cofnąć.',
    save: 'Zapisz zadanie',
    doneTitle: 'Zrobione',
    doneBy: 'Zrobione {{date}} — {{name}}',
  },
  settings: {
    language: 'Język',
    languageHint: 'Zapisujemy wybór w Twoim profilu, więc telefon i tablet otworzą się w tym samym języku.',
    displayName: 'Nazwa wyświetlana',
    displayNameHint: 'Tak zobaczą Cię inne osoby w hodowli — przy wagach i notatkach.',
    displayNameFallback: 'Jeśli zostawisz puste, pokażemy Cię jako „{{name}}”.',
  },
  /**
   * Autonyms — the name of a language written in that language. Identical in
   * every locale on purpose: someone who has landed in a language they cannot
   * read needs to find their own, and "Polish" is no help to them.
   */
  language: {
    pl: 'Polski',
    en: 'English',
  },
  /**
   * The offer to put the app on the home screen, in settings. Android gets a
   * button that installs; an iPhone gets the three steps, because Safari's menu
   * item is the only way in and the app can do no more than point at it.
   */
  install: {
    title: 'Aplikacja na ekranie głównym',
    hint: 'Dodaj Puppy Doctor do ekranu głównego — otworzy się jak aplikacja, na pełnym ekranie.',
    action: 'Dodaj do ekranu głównego',
    iosShare: 'Dotknij ikony udostępniania na pasku przeglądarki:',
    iosAdd: 'Wybierz „Do ekranu początkowego”.',
    iosConfirm: 'Potwierdź przyciskiem „Dodaj”.',
  },
  update: {
    available: 'Dostępna nowa wersja',
    reload: 'Odśwież',
  },
} as const;
